import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Container,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import Header from "../components/Header";
import { getCurrentUser, getIdTokenClaims } from "../common/auth";

const ROLE_LABELS = {
  admin: "Administrador",
  editor: "Editor",
  reader: "Lector",
};

function InfoRow({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
        {value || "—"}
      </Typography>
    </Paper>
  );
}

export default function UserProfile() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const claims = await getIdTokenClaims();
        const user = getCurrentUser();
        const email =
          claims.email ||
          claims["cognito:username"] ||
          user?.getUsername?.() ||
          "";
        const name = claims.name || email.split("@")[0] || "Usuario";
        const role = claims["custom:role"] || "—";

        if (!cancelled) {
          setProfile({
            name,
            email,
            userId: claims.sub || "—",
            role,
            roleLabel: ROLE_LABELS[role] || role,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "No se pudo cargar tu perfil");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const initials = profile?.name
    ? profile.name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "US";

  return (
    <div>
      <Header />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: "#0066cc" }}>{initials}</Avatar>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonOutlinedIcon color="primary" />
              <Typography variant="h4" fontWeight="bold">
                Mi perfil
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Información de tu cuenta de usuario
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={72} />
            <Skeleton variant="rounded" height={72} />
            <Skeleton variant="rounded" height={72} />
          </Stack>
        ) : profile ? (
          <Stack spacing={2}>
            <InfoRow label="Nombre" value={profile.name} />
            <InfoRow label="Email" value={profile.email} />
            <InfoRow label="ID de usuario" value={profile.userId} />
            <InfoRow label="Rol en el workspace" value={profile.roleLabel} />
          </Stack>
        ) : null}
      </Container>
    </div>
  );
}
